import type { HTTPError } from '@core/utils/types/errors';

import Cookie from 'js-cookie';

import getAmplitudeDetails from '../useAmplitude/getAmplitudeDetails';

/**
 * Includes response headers along with the data returned from the fetcher. Use
 * this when you need access to `fetcher` response headers.
 * @example
 * ```ts
 * const response = await fetcher<FetcherResponse<DataType>>('/api/data', { method: 'GET' });
 * console.log(response); // Access data
 * console.log(response.__headers__); // Access response headers
 * ```
 */
export type FetcherResponse<Data> = Data & {
  /**
   * Internal property containing response headers from the fetcher.
   * @internal
   */
  __headers__?: Record<string, string | undefined>;
};

export interface FetcherOptions extends RequestInit {
  isFormData?: boolean;
}

/**
 * Wraps the native fetch API to include necessary headers and error handling when interfacing with our backend services. Returns JSON responses by default.
 *
 * To access response headers, use the `FetcherResponse` type to augment your
 * data type.
 *
 * @example
 * ```ts
 * // Basic usage
 * const data = await fetcher<DataType>('/api/data', { method: 'GET' });
 * console.log(data); // Access data
 *
 * // With response headers
 * const data = await fetcher<FetcherResponse<DataType>>('/api/data', { method: 'GET' });
 * console.log(data.__headers__); // Access response headers
 *
 * // With SWR
 * const { data } = useSWR<FetcherResponse<DataType>>('/api/data', fetcher);
 * console.log(data?.__headers__); // Access response headers
 * ```
 */
async function fetcher<Data>(
  url: Parameters<typeof fetch>[0],
  options: FetcherOptions = {},
): Promise<FetcherResponse<Data>> {
  const { headers, isFormData = false, ...fetchOptions } = options;

  const amplitudeDetails = getAmplitudeDetails();

  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': Cookie.get('XSRF-TOKEN') ?? '',
    'x-amplitude-details': JSON.stringify(amplitudeDetails),
    ...headers,
  };

  // When sending multipart/form-data, we need to remove the default Content-Type header
  if (isFormData && 'Content-Type' in requestHeaders) {
    delete requestHeaders['Content-Type'];
  }

  const res = await fetch(url, {
    headers: requestHeaders,
    ...fetchOptions,
  });

  const contentType = res.headers.get('Content-Type');
  const isJsonResponse = ['application/json', 'application/problem+json'].some(type => contentType?.includes(type));
  const response = isJsonResponse ? ((await res.json()) as FetcherResponse<Data>) : await res.text();

  // Handle unsuccessful responses by rejecting the fetch with a general error
  // object augmented with additional metadata about the status and request.
  if (!res.ok) {
    const error: HTTPError = new Error(`An error occurred while fetching "${url}".`);
    error.info = response;
    error.status = res.status;
    error.statusText = res.statusText;
    error.url = url;
    error.headers = Object.fromEntries(res.headers.entries());
    throw error;
  }

  // Attach response headers to the response object for internal access only.
  if (typeof response !== 'string') {
    try {
      response.__headers__ = Object.fromEntries(res.headers.entries());
    } catch {
      // Ignore errors related to attaching headers.
    }
  }

  return response as FetcherResponse<Data>;
}

export default fetcher;
